import structuredCloneFallback from "@ungap/structured-clone";

// Builtins Safari only shipped in 15.4, for school iPads still on iPadOS 15.0–15.3 (seroval calls
// Object.hasOwn on every server-fn response). Imported first by router.tsx and the PDF workers.

type Predicate<T, This> = (
  this: This | undefined,
  value: T,
  index: number,
  list: ArrayLike<T>,
) => boolean;

function lastIndexWhere<T, This>(
  list: ArrayLike<T>,
  predicate: Predicate<T, This>,
  thisArg?: This,
): number {
  for (let index = list.length - 1; index >= 0; index--) {
    if (predicate.call(thisArg, list[index], index, list)) return index;
  }
  return -1;
}

function at<T>(this: ArrayLike<T>, index: number): T | undefined {
  const offset = Math.trunc(index) || 0;
  const position = offset < 0 ? this.length + offset : offset;
  return position >= 0 && position < this.length ? this[position] : undefined;
}

function findLastIndex<T, This>(
  this: ArrayLike<T>,
  predicate: Predicate<T, This>,
  thisArg?: This,
): number {
  return lastIndexWhere(this, predicate, thisArg);
}

function findLast<T, This>(
  this: ArrayLike<T>,
  predicate: Predicate<T, This>,
  thisArg?: This,
): T | undefined {
  const index = lastIndexWhere(this, predicate, thisArg);
  return index === -1 ? undefined : this[index];
}

const hasOwn: typeof Object.hasOwn = (owner, key) =>
  Object.prototype.hasOwnProperty.call(owner, key);

// Non-enumerable like the native builtins, so for…in loops over arrays stay clean.
function polyfill<Owner extends object, Value>(owner: Owner, name: string, value: Value) {
  if (Reflect.has(owner, name)) return;
  Object.defineProperty(owner, name, { value, configurable: true, writable: true });
}

const typedArrayPrototype: object = Object.getPrototypeOf(Int8Array.prototype);

polyfill(Object, "hasOwn", hasOwn);
for (const prototype of [Array.prototype, String.prototype, typedArrayPrototype]) {
  polyfill(prototype, "at", at);
}
for (const prototype of [Array.prototype, typedArrayPrototype]) {
  polyfill(prototype, "findLast", findLast);
  polyfill(prototype, "findLastIndex", findLastIndex);
}
polyfill(globalThis, "structuredClone", structuredCloneFallback);
